import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { VReadOnly } from '@client/components/_shared/forms/view-form/VReadOnly';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CardTitle } from '@client/components/_ui/display/CardTitle';
import { QueryState } from '@client/components/_ui/feedback/QueryState';
import { CostCodeTag } from '@client/components/claims/financials/CostCodeTag';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { createFormStore } from '@client/stores/FormStore';
import { type RouterOutputs, trpc } from '@client/trpc/client';
import { cn } from '@client/utils';
import { type ConfigurableCompensationPaymentField, isCompensationPaymentFieldVisible } from '@client/utils/financials';
import { COMPENSATION_PAYMENT_USE_TYPES_LABELS } from '@shared/constants/business/compensation-payment';
import { toYesNo } from '@shared/utils/core/general';
import { createFileRoute } from '@tanstack/react-router';
import { Card } from 'antd';
import { ClipboardCheck, FileText, Receipt } from 'lucide-react';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/compensations/details/$compensationId/financials/compensation-payments/details/$compensationPaymentId',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Payment item details',
        },
    },
    params: {
        parse({ compensationPaymentId }) {
            return {
                compensationPaymentId: BigInt(compensationPaymentId),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CompensationPaymentDetails />
        </PageController>
    );
}

type Compensation = NonNullable<RouterOutputs['compensation']['getCompensation']>;
type CompensationPayment = NonNullable<RouterOutputs['compensationPayment']['getCompensationPayment']>;

function isFieldVisible(name: ConfigurableCompensationPaymentField, compensation: Compensation) {
    return isCompensationPaymentFieldVisible(name, {
        transactionNature: compensation.TransactionType.transactionNature,
        isRegisteredForGst: compensation.isRegisteredForGst,
        payeeType: compensation.payeeType,
    });
}

const compensationPaymentFormStore = createFormStore<CompensationPayment>();

function CompensationPaymentDetails() {
    const { compensationId, compensationPaymentId } = Route.useParams();
    const { data: compensation } = trpc.compensation.getCompensation.useQuery({ id: compensationId });
    const {
        data: compensationPayment,
        isPending,
        error,
        isFetching,
        refetch,
    } = trpc.compensationPayment.getCompensationPayment.useQuery({
        id: compensationPaymentId,
    });
    const { formatCurrency, formatPercentage } = useFormatter();
    const navigate = useNavigate();

    compensationPaymentFormStore.useInit({
        data: compensationPayment,
        isLoading: isPending,
        isReadOnly: true,
        async onReload() {
            await refetch();
        },
        async onSave() {
            return { success: true };
        },
    });

    const hasTaxComplianceFields =
        compensation &&
        (isFieldVisible('isGSTApplicable', compensation) ||
            isFieldVisible('doYouHoldValidTaxInvoice', compensation) ||
            isFieldVisible('isSupplyUnderMinimum', compensation) ||
            (isFieldVisible('isThisPurchaseOfMotorVehicle', compensation) &&
                compensation.Claim?.ProductType?.useCarLimitLogic) ||
            (isFieldVisible('isThisPurchaseOfRealPropertyUnderMarginScheme', compensation) &&
                compensation.Claim?.ProductType?.useMarginScheme) ||
            isFieldVisible('isPreApproved', compensation));

    return (
        <OverlayPage
            afterClose={() => navigate({ moveInMatches: -1 })}
            title="Payment item details"
            size="middle"
            footer={({ onClose }) => <Button onClick={onClose}>Close</Button>}
        >
            <QueryState query={{ data: compensationPayment, error, isFetching, refetch }}>
                {() => (
                    <div className="flex flex-col gap-4">
                        <Card
                            type="inner"
                            size="small"
                            title={<CardTitle icon={<Receipt />} title="Summary" />}
                            classNames={{
                                body: cn(
                                    'grid gap-x-6 gap-y-4',
                                    compensation?.isRegisteredForGst ? 'grid-cols-3' : 'grid-cols-1',
                                ),
                            }}
                        >
                            {compensation?.isRegisteredForGst ? (
                                <>
                                    <VReadOnly
                                        label="Net amount"
                                        field="netAmount"
                                        formStore={compensationPaymentFormStore}
                                        formatValueFn={(value) => formatCurrency(value)}
                                    />
                                    <VReadOnly
                                        label="GST amount"
                                        field="gstAmount"
                                        formStore={compensationPaymentFormStore}
                                        formatValueFn={(value) => formatCurrency(value)}
                                    />
                                    <VReadOnly
                                        label="Total amount"
                                        field="totalAmount"
                                        formStore={compensationPaymentFormStore}
                                        formatValueFn={(value) => formatCurrency(value)}
                                    />
                                </>
                            ) : (
                                <VReadOnly
                                    label="Amount"
                                    field="totalAmount"
                                    formStore={compensationPaymentFormStore}
                                    formatValueFn={(value) => formatCurrency(value)}
                                />
                            )}
                        </Card>

                        <Card
                            type="inner"
                            size="small"
                            title={<CardTitle icon={<FileText />} title="Details" />}
                            classNames={{ body: 'flex flex-col gap-4' }}
                        >
                            <VReadOnly
                                label="Reserve"
                                formStore={compensationPaymentFormStore}
                                formatValueFn={(_, item) => (
                                    <span className="flex items-center gap-1">
                                        Reserve - <CostCodeTag costCode={item?.Reserve?.costCodeFullName} />
                                    </span>
                                )}
                            />
                            <VReadOnly
                                label="Description"
                                field="description"
                                formStore={compensationPaymentFormStore}
                            />

                            {isFieldVisible('useType', compensation!) && (
                                <VReadOnly
                                    label="Use type"
                                    field="useType"
                                    formStore={compensationPaymentFormStore}
                                    helpMessage="Indicates how the item is used. The ITCE % should align with the extent to which the item is used for business purposes."
                                    formatValueFn={(_, item) =>
                                        item?.useType ? COMPENSATION_PAYMENT_USE_TYPES_LABELS[item.useType] : null
                                    }
                                />
                            )}

                            {isFieldVisible('inputTaxCreditEntitlement', compensation!) && (
                                <VReadOnly
                                    label={`${compensation!.payeeType === 'POLICY_HOLDER' ? 'Policy holder' : 'Third party'} ITCE %`}
                                    formStore={compensationPaymentFormStore}
                                    helpMessage={`The ITCE % of the ${compensation!.payeeType === 'POLICY_HOLDER' ? 'policy holder' : 'third party'}. For business or mixed use items, the ITCE may be adjusted to reflect the level of business use.`}
                                    formatValueFn={(_, item) =>
                                        item?.inputTaxCreditEntitlement
                                            ? formatPercentage(Number(item.inputTaxCreditEntitlement) / 100)
                                            : null
                                    }
                                />
                            )}
                        </Card>

                        {hasTaxComplianceFields && (
                            <Card
                                type="inner"
                                size="small"
                                title={<CardTitle icon={<ClipboardCheck />} title="Tax compliance" />}
                                classNames={{ body: 'grid max-w-lg grid-cols-2 gap-x-6 gap-y-4' }}
                            >
                                {isFieldVisible('isGSTApplicable', compensation) && (
                                    <VReadOnly
                                        label="GST applicable"
                                        field="isGSTApplicable"
                                        formStore={compensationPaymentFormStore}
                                        helpMessage="When enabled, GST is applied to this payment item."
                                        formatValueFn={(value) => toYesNo(value)}
                                    />
                                )}

                                {isFieldVisible('doYouHoldValidTaxInvoice', compensation) && (
                                    <VReadOnly
                                        label="Valid tax invoice"
                                        field="doYouHoldValidTaxInvoice"
                                        formStore={compensationPaymentFormStore}
                                        helpMessage={
                                            <>
                                                <p>
                                                    Indicates whether a valid tax invoice has been received from the
                                                    supplier for this item. A valid tax invoice must show the supplier's
                                                    ABN, identify the goods or services, and clearly state the GST
                                                    amount charged.
                                                </p>
                                                <p>
                                                    An invoice that is more than 4 years old is not considered a valid
                                                    tax invoice.
                                                </p>
                                            </>
                                        }
                                        formatValueFn={(value) => toYesNo(value)}
                                    />
                                )}

                                {isFieldVisible('isSupplyUnderMinimum', compensation) && (
                                    <VReadOnly
                                        label="Supply under $82.50"
                                        field="isSupplyUnderMinimum"
                                        formStore={compensationPaymentFormStore}
                                        helpMessage="If the gross amount of this item is less than $82.50 including GST, no tax invoice is required to claim ITC/DA."
                                        formatValueFn={(value) => toYesNo(value)}
                                    />
                                )}

                                {isFieldVisible('isThisPurchaseOfMotorVehicle', compensation) &&
                                    compensation.Claim?.ProductType?.useCarLimitLogic && (
                                        <VReadOnly
                                            label="Motor vehicle purchase"
                                            field="isThisPurchaseOfMotorVehicle"
                                            formStore={compensationPaymentFormStore}
                                            helpMessage="When enabled, the system checks the luxury car tax threshold and calculates the ITC on this item accordingly."
                                            formatValueFn={(value) => toYesNo(value)}
                                        />
                                    )}

                                {isFieldVisible('isThisPurchaseOfRealPropertyUnderMarginScheme', compensation) &&
                                    compensation.Claim?.ProductType?.useMarginScheme && (
                                        <VReadOnly
                                            label="Real property margin scheme"
                                            field="isThisPurchaseOfRealPropertyUnderMarginScheme"
                                            formStore={compensationPaymentFormStore}
                                            helpMessage="When enabled, the GST amount for this item is manually specified as it falls under the property margin scheme."
                                            formatValueFn={(value) => toYesNo(value)}
                                        />
                                    )}

                                {isFieldVisible('isPreApproved', compensation) && (
                                    <VReadOnly
                                        label="Pre-approved"
                                        field="isPreApproved"
                                        formStore={compensationPaymentFormStore}
                                        helpMessage="Indicates whether the insurer has agreed to the use, replacement, or settlement value of this item before payment. Pre-approval confirms that the item meets claim and policy conditions."
                                        formatValueFn={(value) => toYesNo(value)}
                                    />
                                )}
                            </Card>
                        )}
                    </div>
                )}
            </QueryState>
        </OverlayPage>
    );
}
